const chapter = {
  id: 'biodiversity-conservation',
  slug: 'biodiversity-conservation',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Biodiversity and Conservation',
        hindi: 'जैव विविधता और संरक्षण',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Ecology', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'levels',
      title: {
        english: 'Levels of Biodiversity',
        hindi: 'जैव विविधता के स्तर',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Term **biodiversity** popularised by sociobiologist **Edward Wilson**.',
        hindi: 'शब्द **जैव विविधता** समाजशास्त्री **एडवर्ड विल्सन** द्वारा लोकप्रिय हुआ।',
            },
            {
              english: 'Three levels (Whittaker, Rosenzweig):',
        hindi: 'तीन स्तर (व्हिटेकर, रोसेनज़वेग):',
            },
            {
              english: '• **Genetic diversity** — variation in genes within a species (e.g. *Rauwolfia vermifolia* in Himalayas → different reserpine concentrations; India has > 50,000 genetically distinct strains of rice, 1000 of mango).',
        hindi: '• **आनुवंशिक विविधता** - एक प्रजाति के भीतर जीन में भिन्नता (उदाहरण के लिए हिमालय में *राउवोल्फिया वर्मीफोलिया* → विभिन्न रिसरपाइन सांद्रता; भारत में चावल की आनुवंशिक रूप से भिन्न उपभेद 50,000, आम की 1000 से अधिक हैं)।',
            },
            {
              english: '• **Species diversity** — variety of species in a region (Western Ghats > Eastern Ghats in amphibian species).',
        hindi: '• **प्रजाति विविधता** — एक क्षेत्र में प्रजातियों की विविधता (उभयचर प्रजातियों में पश्चिमी घाट > पूर्वी घाट)।',
            },
            {
              english: '• **Ecological / community diversity** — variety of habitats / niches / communities (India with desert + rain forest + mangrove + coral reef + alpine meadows + wetland > Scandinavia).',
        hindi: '• **पारिस्थितिक/सामुदायिक विविधता** - आवासों/आलों/समुदायों की विविधता (रेगिस्तान + वर्षा वन + मैंग्रोव + मूंगा चट्टान + अल्पाइन घास के मैदान + आर्द्रभूमि > स्कैंडिनेविया वाला भारत)।',
            },
          ],
        },
      ],
    },
    {
      id: 'global-pattern',
      title: {
        english: 'How Many Species & Where?',
        hindi: 'कितनी प्रजातियां और कहां?',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'IUCN (2004): > **1.5 million** species described.',
        hindi: 'IUCN (2004): > **1.5 मिलियन** प्रजातियों का वर्णन किया गया है। **रॉबर्ट मे** का अनुमान: वैश्विक प्रजातियां ≈ **7 मिलियन** (इसलिए > 70% अभी तक खोजा जाना बाकी है)।',
            },
            {
              english: '**Robert May** estimate: global species ≈ **7 million** (so > 70 % yet to be discovered).',
        hindi: '**रॉबर्ट मे** का अनुमान: वैश्विक प्रजातियाँ ≈ **7 मिलियन** (इसलिए > 70% अभी तक खोजा जाना बाकी है)।',
            },
            {
              english: 'Of described — animals **70 %**, plants ~22 %; among animals **insects ~70 %** (over 1 million) — every 5th organism on Earth is a beetle.',
        hindi: 'वर्णित - जानवर **70%**, पौधे ~22%; जानवरों में **कीड़े ~70%** (1 मिलियन से अधिक) - पृथ्वी पर हर 5वां जीव एक भृंग है।',
            },
            {
              english: 'India has **2.4 % of world\'s land** but **8.1 % of global species diversity** → one of 12 mega-diverse countries.',
        hindi: 'भारत के पास दुनिया की **2.4% भूमि** है** लेकिन **8.1% वैश्विक प्रजाति विविधता** → 12 मेगा-विविध देशों में से एक है।',
            },
            {
              english: 'India total ~ 45,000 plants + 91,000 animals = **1,35,000 species** described; estimated ~ 1,00,000 plants + 3,00,000 animals more remain to be discovered.',
        hindi: 'भारत में कुल ~45,000 पौधे + 91,000 animals = **1,35,000 प्रजातियाँ** वर्णित हैं; अनुमानित ~ 1,00,000 पौधे + 3,00,000 जानवर और खोजे जाने बाकी हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'patterns',
      title: {
        english: 'Patterns of Biodiversity',
        hindi: 'जैव विविधता के प्रतिरूप',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Latitudinal gradient** — diversity decreases as we move from equator to poles.',
        hindi: '**अक्षांशीय ढाल** - जैसे-जैसे हम भूमध्य रेखा से ध्रुवों की ओर बढ़ते हैं, विविधता कम होती जाती है।',
            },
            {
              english: '• Tropics (between Tropic of Cancer & Capricorn) harbour more species than temperate or polar.',
        hindi: '• उष्णकटिबंधीय (कर्क और मकर रेखा के बीच) समशीतोष्ण या ध्रुवीय की तुलना में अधिक प्रजातियों को आश्रय देते हैं।',
            },
            {
              english: '• Colombia (~10° N) has ~1400 bird species; New York (41° N) — 105; Greenland (71° N) — 56. Amazon rain forest has ~40,000 plant species, 3000 fishes, 1300 birds, 427 mammals, 427 amphibians, 378 reptiles, > 1,25,000 invertebrates.',
        hindi: '• कोलंबिया (~10° उत्तर) में ~1400 पक्षी प्रजातियाँ हैं; न्यूयॉर्क (41° उत्तर)-105; ग्रीनलैंड (71° उत्तर) - 56. अमेज़ॅन वर्षा वन में ~40,000 पौधों की प्रजातियाँ, 3000 मछलियाँ, 1300 पक्षी, 427 स्तनधारी, 427 उभयचर, 378 सरीसृप, > 1,25,000 अकशेरुकी जीव हैं।',
            },
            {
              english: 'Reasons tropics are more diverse:',
        hindi: 'उष्णकटिबंधीय क्षेत्रों के अधिक विविध होने के कारण:',
            },
            {
              english: '• Long evolutionary time (temperate disturbed by glaciation).',
        hindi: '• लंबा विकासवादी समय (हिमनद द्वारा परेशान शीतोष्ण)।',
            },
            {
              english: '• Less seasonal, more constant & predictable environment → niche specialisation.',
        hindi: '• कम मौसमी, अधिक स्थिर और पूर्वानुमान योग्य वातावरण → विशिष्ट विशेषज्ञता।',
            },
            {
              english: '• More solar energy → higher productivity.',
        hindi: '• अधिक सौर ऊर्जा → उच्च उत्पादकता।',
            },
            {
              english: '**Species-area relationship** (Alexander von Humboldt):',
        hindi: '**प्रजाति-क्षेत्र संबंध** (अलेक्जेंडर वॉन हम्बोल्ट):',
            },
          ],
        },
        {
          type: 'equation',
          latex: '\\log S = \\log C + Z \\log A',
          label: {
            english: 'Power law of species (S) vs area (A); Z = slope (regression coefficient), C = intercept',
        hindi: 'प्रजातियों का शक्ति नियम (एस) बनाम क्षेत्र (ए); Z = slope (regression coefficient), C = intercept',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Z = 0.1 - 0.2** for small areas (within continent).',
        hindi: '**Z = 0.1 - 0.2** छोटे क्षेत्रों के लिए (महाद्वीप के भीतर)।',
            },
            {
              english: '**Z = 0.6 - 1.2** for large areas (entire continents).',
        hindi: '**Z = 0.6 - 1.2** बड़े क्षेत्रों (संपूर्ण महाद्वीप) के लिए।',
            },
            {
              english: 'Species-area relation on log-log scale → straight line.',
        hindi: 'लॉग-लॉग स्केल पर प्रजाति-क्षेत्र संबंध → सीधी रेखा।',
            },
          ],
        },
      ],
    },
    {
      id: 'importance',
      title: {
        english: 'Importance of Species Diversity',
        hindi: 'प्रजाति विविधता का महत्व',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Paul Ehrlich — Rivet popper hypothesis** — ecosystem = airplane; each species = rivet. Loss of few rivets may not crash plane but loss of key rivets (esp. of wings) is dangerous.',
        hindi: '**पॉल एर्लिच - रिवेट पॉपर परिकल्पना** - ecosystem = airplane; प्रत्येक species = rivet. कुछ रिवेट्स के नष्ट होने से विमान दुर्घटनाग्रस्त नहीं हो सकता है, लेकिन मुख्य रिवेट्स (विशेषकर पंखों) का नुकसान खतरनाक है।',
            },
            {
              english: 'David Tilman — long-term ecosystem experiments: more diverse plots had less variation in biomass + higher productivity.',
        hindi: 'डेविड टिलमैन - दीर्घकालिक पारिस्थितिकी तंत्र प्रयोग: अधिक विविध भूखंडों में बायोमास में कम भिन्नता + उच्च उत्पादकता थी।',
            },
            {
              english: 'Stable community: little year-to-year variation, resists invasions, resists disturbances (drought).',
        hindi: 'स्थिर समुदाय: साल-दर-साल थोड़ा बदलाव, आक्रमणों का प्रतिरोध, गड़बड़ी (सूखा) का प्रतिरोध करता है।',
            },
            {
              english: 'Ecosystem services: pollination by bees + birds, climate regulation, prevention of soil erosion, drug discovery — economic + ethical (intrinsic) + ecological value.',
        hindi: 'पारिस्थितिकी तंत्र सेवाएं: मधुमक्खियों + पक्षियों द्वारा परागण, जलवायु विनियमन, मिट्टी के कटाव की रोकथाम, दवा की खोज - आर्थिक + नैतिक (आंतरिक) + पारिस्थितिक मूल्य।',
            },
          ],
        },
      ],
    },
    {
      id: 'loss',
      title: {
        english: 'Loss of Biodiversity',
        hindi: 'जैव विविधता की हानि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'IUCN Red List 2004: 784 species extinct in last 500 yrs (338 vertebrates, 359 invertebrates, 87 plants).',
        hindi: 'IUCN लाल सूची 2004: पिछले 500 वर्षों में 784 प्रजातियाँ विलुप्त हो गईं (338 कशेरुक, 359 अकशेरुकी, 87 पौधे)।',
            },
            {
              english: 'Recent extinctions: Dodo (Mauritius), Quagga (Africa), Thylacine (Australia), Steller\'s Sea Cow (Russia), 3 subspecies of tiger (Bali, Javan, Caspian).',
        hindi: 'हाल की विलुप्ति: डोडो (मॉरीशस), क्वागा (अफ्रीका), थायलासीन (ऑस्ट्रेलिया), स्टेलर सी काउ (रूस), बाघ की 3 उपप्रजातियां (बाली, जावन, कैस्पियन)।',
            },
            {
              english: '**6th mass extinction** in progress — current rate is **100-1000 ×** pre-human rate.',
        hindi: '**छठा सामूहिक विलोपन** प्रगति पर है - वर्तमान दर **100-1000 ×** पूर्व-मानव दर है। **चार कारण (द एविल क्वार्टेट)** - एडवर्ड विल्सन:',
            },
            {
              english: '**Four causes (The Evil Quartet)** — Edward Wilson:',
        hindi: '**चार कारण (द एविल क्वार्टेट)** - एडवर्ड विल्सन:',
            },
            {
              english: '• **Habitat loss & fragmentation** — most important. Tropical rain forests reduced from 14 % → 6 % of land. Amazon rain forest = "lungs of planet" cut & burned to grow soybeans / grazing for beef cattle.',
        hindi: '• **पर्यावास हानि और विखंडन** — सबसे महत्वपूर्ण। उष्णकटिबंधीय वर्षा वन 14% → 6% भूमि से कम हो गये। अमेज़ॅन बारिश forest = "lungs of planet" cut & burned to grow soybeans / grazing for beef cattle।',
            },
            {
              english: '• **Overexploitation** — Steller\'s sea cow, passenger pigeon hunted to extinction; many marine fishes today.',
        hindi: '• **अत्यधिक दोहन** - स्टेलर की समुद्री गाय, यात्री कबूतर को विलुप्त होने तक शिकार किया गया; आज बहुत सी समुद्री मछलियाँ।',
            },
            {
              english: '• **Alien species invasions** — Nile Perch in Lake Victoria → 200 cichlid fish extinct; Parthenium, Lantana, water hyacinth (Eichhornia) in India; African catfish (Clarias gariepinus) threatening native catfishes.',
        hindi: '• **विदेशी प्रजातियों का आक्रमण** - विक्टोरिया झील में नील पर्च → 200 सिक्लिड मछलियाँ विलुप्त; भारत में पार्थेनियम, लैंटाना, जलकुंभी (इचोर्निया); अफ़्रीकी कैटफ़िश (क्लैरियास गैरीपिनस) देशी कैटफ़िश के लिए ख़तरा है।',
            },
            {
              english: '• **Co-extinctions** — extinction of plant brings down host-specific pollinator/parasite (fig–wasp).',
        hindi: '• **सह-विलुप्तता** - पौधे के विलुप्त होने से मेजबान-विशिष्ट pollinator/parasite (अंजीर-ततैया) में कमी आती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'conservation-why',
      title: {
        english: 'Why Conserve Biodiversity?',
        hindi: 'जैव विविधता का संरक्षण क्यों करें?',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Narrowly utilitarian** — direct economic benefits (food, firewood, fibre, drugs ~25 % of US drugs from plants).',
        hindi: '**संकीर्ण रूप से उपयोगितावादी** - प्रत्यक्ष आर्थिक लाभ (भोजन, जलाऊ लकड़ी, फाइबर, दवाएं ~ पौधों से अमेरिकी दवाओं का 25%)।',
            },
            {
              english: '**Broadly utilitarian** — ecosystem services. Amazon produces **20 % of total atmospheric O₂**; pollination by bees, hummingbirds, bats; aesthetic pleasure (walking through forest, flowers in bloom).',
        hindi: '**मोटे तौर पर उपयोगितावादी** - पारिस्थितिकी तंत्र सेवाएँ। अमेज़न कुल वायुमंडलीय O₂** का **20% उत्पादन करता है; मधुमक्खियों, चिड़ियों, चमगादड़ों द्वारा परागण; सौंदर्यात्मक आनंद (जंगल में घूमना, खिले हुए फूल)।',
            },
            {
              english: '**Ethical** — every species has intrinsic value; we have moral duty to pass on biological legacy to future generations.',
        hindi: '**नैतिक** - प्रत्येक प्रजाति का आंतरिक मूल्य होता है; भावी पीढ़ियों को जैविक विरासत सौंपना हमारा नैतिक कर्तव्य है।',
            },
          ],
        },
      ],
    },
    {
      id: 'conservation-how',
      title: {
        english: 'How Do We Conserve?',
        hindi: 'हम कैसे संरक्षण करें?',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**In-situ (on-site)** — protection of entire ecosystem.',
        hindi: '**इन-सीटू (ऑन-साइट)** - संपूर्ण पारिस्थितिकी तंत्र की सुरक्षा।',
            },
            {
              english: '• **Biodiversity hotspots** (Norman Myers) — high endemism + serious threat. World total = **34 hotspots** (originally 25). India has **4**: Western Ghats & Sri Lanka, Indo-Burma, Himalaya, Sundaland (Nicobar Islands).',
        hindi: '• **जैव विविधता हॉटस्पॉट** (नॉर्मन मायर्स) - उच्च स्थानिकवाद + गंभीर खतरा। विश्व total = **34 hotspots** (originally 25). भारत में **4** हैं: पश्चिमी घाट और श्रीलंका, इंडो-बर्मा, हिमालय, सुंदरलैंड (निकोबार द्वीप)।',
            },
            {
              english: '• Hotspots cover < 2 % of Earth\'s land but harbour very high biodiversity.',
        hindi: '• हॉटस्पॉट पृथ्वी की 2% भूमि को कवर करते हैं लेकिन बहुत अधिक जैव विविधता रखते हैं।',
            },
            {
              english: '• **Protected areas in India**: 14 biosphere reserves, 90 national parks, 448 wildlife sanctuaries.',
        hindi: '• **भारत में संरक्षित क्षेत्र**: 14 बायोस्फीयर रिजर्व, 90 राष्ट्रीय उद्यान, 448 वन्यजीव अभयारण्य।',
            },
            {
              english: '• Sacred groves — last refuge of large number of rare/threatened plants (Khasi & Jaintia Hills in Meghalaya, Aravalli Hills, Western Ghat regions of Karnataka & Maharashtra, Sarguja, Chanda & Bastar in MP).',
        hindi: '• पवित्र उपवन - बड़ी संख्या में rare/threatened पौधों (मेघालय में खासी और जयन्तिया पहाड़ियाँ, अरावली पहाड़ियाँ, कर्नाटक और महाराष्ट्र के पश्चिमी घाट क्षेत्र, मप्र में सरगुजा, चंदा और बस्तर) का अंतिम आश्रय। **एक्स-सीटू (ऑफ-साइट)** - विशेष सेटिंग में rare/threatened को धमकी दी गई।',
            },
            {
              english: '**Ex-situ (off-site)** — threatened animals/plants in special setting.',
        hindi: '**एक्स-सीटू (ऑफ-साइट)** - विशेष सेटिंग में animals/plants को धमकी दी गई।',
            },
            {
              english: '• Zoological parks, botanical gardens, wildlife safari parks.',
        hindi: '• प्राणी उद्यान, वनस्पति उद्यान, वन्यजीव सफारी पार्क।',
            },
            {
              english: '• **Cryopreservation** of gametes in liquid N₂ at −196 °C; eggs fertilised in vitro; tissue culture for plants; **seed banks**.',
        hindi: '• -196 डिग्री सेल्सियस पर तरल N₂ में युग्मकों का **क्रायोप्रिजर्वेशन**; इन विट्रो में निषेचित अंडे; पौधों के लिए ऊतक संवर्धन; **बीज बैंक**। **अंतर्राष्ट्रीय प्रयास**: पृथ्वी शिखर सम्मेलन (रियो, 1992) + सतत विकास पर विश्व शिखर सम्मेलन (जोहान्सबर्ग, 2002) - जैव विविधता संरक्षण के प्रति प्रतिबद्धता।',
            },
            {
              english: '**International effort**: Earth Summit (Rio, 1992) + World Summit on Sustainable Development (Johannesburg, 2002) — pledged commitment to biodiversity conservation.',
        hindi: '**अंतर्राष्ट्रीय प्रयास**: पृथ्वी शिखर सम्मेलन (रियो, 1992) + सतत विकास पर विश्व शिखर सम्मेलन (जोहान्सबर्ग, 2002) - जैव विविधता संरक्षण के प्रति प्रतिबद्धता।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\log S = \\log C + Z \\log A',
      desc: {
        english: 'Species-area: Z = 0.1-0.2 (small), 0.6-1.2 (continents)',
        hindi: 'प्रजाति-क्षेत्र: Z = 0.1-0.2 (छोटा), 0.6-1.2 (महाद्वीप)',
      },
    },
    {
      latex: '\\text{Global species} \\approx 1.5 \\text{ M described}; \\approx 7 \\text{ M (May estimate)}',
      desc: {
        english: '',
        hindi: 'वैश्विक प्रजाति 1.5 एम का वर्णन किया गया; 7 एम (मई अनुमान)',
      },
    },
    {
      latex: '\\text{India: 2.4\\% land, 8.1\\% global biodiversity}',
      desc: {
        english: 'Mega-diverse country',
        hindi: 'विशाल विविधता वाला देश',
      },
    },
    {
      latex: '\\text{Insects} \\approx 70\\% \\text{ of all animals}',
      desc: {
        english: '',
        hindi: 'सभी जानवरों में से 70% कीड़े हैं',
      },
    },
    {
      latex: '\\text{Evil Quartet: Habitat loss > Overexploitation > Alien invasion > Co-extinction}',
      desc: {
        english: '',
        hindi: 'दुष्ट चौकड़ी: पर्यावास हानि > अत्यधिक दोहन > विदेशी आक्रमण > सह-विलुप्त होना',
      },
    },
    {
      latex: '\\text{34 global hotspots; India = 4}',
      desc: {
        english: 'Western Ghats-Sri Lanka, Indo-Burma, Himalaya, Sundaland',
        hindi: 'पश्चिमी घाट-श्रीलंका, इंडो-बर्मा, हिमालय, सुंदरलैंड',
      },
    },
    {
      latex: '\\text{Cryopreservation at } -196\\text{°C in liquid } N_2',
      desc: {
        english: 'Ex-situ',
        hindi: 'पूर्व-स्थिति',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Hotspot',
      problem: {
        english: 'Which of the following is NOT a biodiversity hotspot of India?',
        hindi: 'निम्नलिखित में से कौन भारत का जैव विविधता हॉटस्पॉट नहीं है?',
      },
      answer: {
        english: 'Aravalli Hills',
        hindi: 'अरावली पहाड़ियाँ',
      },
      solution: {
        english: 'India\'s 4 hotspots: Western Ghats-Sri Lanka, Indo-Burma, Himalaya, Sundaland.',
        hindi: 'भारत के 4 हॉटस्पॉट: पश्चिमी घाट-श्रीलंका, इंडो-बर्मा, हिमालय, सुंदरलैंड।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Quartet',
      problem: {
        english: 'Most important cause of biodiversity loss is:',
        hindi: 'जैव विविधता हानि का सबसे महत्वपूर्ण कारण है:',
      },
      answer: {
        english: 'Habitat loss and fragmentation',
        hindi: 'पर्यावास हानि और विखंडन',
      },
      solution: {
        english: 'Wilson\'s Evil Quartet — habitat destruction is dominant.',
        hindi: 'विल्सन की दुष्ट चौकड़ी - निवास स्थान का विनाश प्रमुख है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Pattern',
      problem: {
        english: 'Species-area relationship on log-log scale gives:',
        hindi: 'लॉग-लॉग स्केल पर प्रजाति-क्षेत्र संबंध देता है:',
      },
      answer: {
        english: 'Straight line with slope Z',
        hindi: 'ढलान Z के साथ सीधी रेखा',
      },
      solution: {
        english: 'log S = log C + Z log A.',
        hindi: 'लॉग एस = लॉग सी + जेड लॉग ए।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Hypothesis',
      problem: {
        english: 'Rivet popper hypothesis was proposed by:',
        hindi: 'रिवेट पॉपर परिकल्पना किसके द्वारा प्रस्तावित की गई थी:',
      },
      answer: {
        english: 'Paul Ehrlich',
        hindi: 'पॉल एर्लिच',
      },
      solution: {
        english: 'Each species = rivet on airplane.',
        hindi: 'प्रत्येक प्रजाति = हवाई जहाज पर कीलक।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Conservation',
      problem: {
        english: 'Cryopreservation of gametes is done at:',
        hindi: 'युग्मकों का क्रायोप्रिजर्वेशन यहां किया जाता है:',
      },
      answer: {
        english: '−196 °C in liquid nitrogen',
        hindi: '-196 डिग्री सेल्सियस तरल नाइट्रोजन में',
      },
      solution: {
        english: 'Ex-situ conservation method.',
        hindi: 'पूर्व-स्थिति संरक्षण विधि.',
      },
    },
  ],
  next: {
    slug: 'sexual-reproduction-flowering-plants',
    title: 'Sexual Reproduction in Flowering Plants',
  },
};

export default chapter;
